import { PAP_Cancel_AddStackSection } from "@mirage/analytics/events/types/cancel_add_stack_section";
import { DragDropList } from "@mirage/drag-and-drop/DragDropList";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import classnames from "classnames";
import { useCallback } from "react";
import { FancyInlineEdit } from "./FancyInlineEdit";
import styles from "./Navigation.module.css";

import type { Cancel_AddStackSection } from "@mirage/analytics/events/types/cancel_add_stack_section";
import type { DragDropListItem } from "@mirage/drag-and-drop/DragDropList";
import type { SectionsActionType } from "../../types/FullScreenTypes";

export const NavigationList = ({
  listItems,
  hasWritePermissions,
  onDrop,
  inlineCreateSection,
  createButton,
}: {
  listItems: DragDropListItem[];
  hasWritePermissions?: boolean;
  onDrop: (result: DragDropListItem[]) => void;
  inlineCreateSection: React.ReactNode;
  createButton?: React.ReactNode;
}) => {
  return (
    <>
      <DragDropList
        droppableId="sectionsNav"
        items={listItems}
        dragDisabled={!hasWritePermissions}
        getListContainerProps={() => ({
          className: styles.sectionsNavDragDrop,
        })}
        getItemContainerProps={(isDragging) =>
          isDragging
            ? { className: styles.sectionsNavItemIsDragging }
            : undefined
        }
        onDrop={onDrop}
      />
      {inlineCreateSection}
      {createButton}
    </>
  );
};

export const InlineCreateSection = ({
  setIsEditing,
  setCurrentAction,
  onCreateSection,
  isAugustRevisionEnabled,
  cancelEditPAPProps,
}: {
  setIsEditing: (isEditing: boolean) => void;
  setCurrentAction: (action: SectionsActionType | null) => void;
  onCreateSection: (title: string) => void;
  isAugustRevisionEnabled: boolean;
  cancelEditPAPProps?: Cancel_AddStackSection["properties"];
}) => {
  const reportStackPapEvent = useReportStackPapEvent();

  const handleCreateSection = useCallback(
    (title: string) => {
      setIsEditing(false);
      setCurrentAction(null);
      onCreateSection(title);
    },
    [onCreateSection, setCurrentAction, setIsEditing],
  );

  return (
    <div
      className={classnames(styles.fancyEditInput, {
        [styles.augustRevision]: isAugustRevisionEnabled,
      })}
    >
      <FancyInlineEdit
        onSubmit={handleCreateSection}
        title=""
        onCancel={() => {
          setIsEditing(false);
          setCurrentAction(null);
          reportStackPapEvent(PAP_Cancel_AddStackSection(cancelEditPAPProps));
        }}
        data-testid="Stacks-createSectionInput"
      />
    </div>
  );
};
